import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import * as LucideIcons from 'lucide-react';

export const Icons = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedIcon, setCopiedIcon] = useState('');

  // Get all Lucide icons
  const iconEntries = Object.entries(LucideIcons).filter(([name]) => 
    name !== 'createLucideIcon' && 
    name !== 'default' &&
    typeof LucideIcons[name] === 'function'
  );

  const filteredIcons = iconEntries.filter(([name]) =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyToClipboard = (iconName) => {
    const importCode = `import { ${iconName} } from 'lucide-react';\n\n<${iconName} size={24} />`;
    navigator.clipboard.writeText(importCode);
    setCopiedIcon(iconName);
    setTimeout(() => setCopiedIcon(''), 2000);
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-12 animate-slide-up">
          <div className="w-20 h-20 bg-[rgba(0,168,255,0.2)] rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ boxShadow: '0 0 30px rgba(0,168,255,0.5)' }}>
            <LucideIcons.Sparkles className="text-[#00a8ff]" size={40} style={{ filter: 'drop-shadow(0 0 10px #00a8ff)' }} />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold led-text animate-led-pulse mb-6" style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #4cd3ff' }}>
            ICON LIBRARY
          </h1>
          <p className="text-xl text-[#00a8ff] mb-8" style={{ textShadow: '0 0 10px #00a8ff' }}>
            Browse and copy {iconEntries.length}+ Lucide React icons
          </p>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <LucideIcons.Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#00a8ff]" size={24} />
            <input
              type="text"
              placeholder="Search icons (e.g., Home, User, Settings)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-4 h-14 bg-[rgba(10,37,64,0.9)] border-2 border-[#00a8ff] text-white text-lg rounded-xl focus:ring-4 focus:ring-[#00a8ff] outline-none"
              style={{ boxShadow: '0 0 20px rgba(0,168,255,0.5)' }}
            />
          </div>
        </div>

        {/* Icons Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          {filteredIcons.map(([name, IconComponent]) => (
            <Card 
              key={name}
              onClick={() => copyToClipboard(name)}
              className="group bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl hover:shadow-[0_0_40px_#00a8ff] transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}
            >
              <CardContent className="p-6 flex flex-col items-center justify-center min-h-[140px]">
                <div className="w-12 h-12 flex items-center justify-center mb-3 text-[#00a8ff] group-hover:scale-125 group-hover:rotate-12 transition-all duration-300">
                  <IconComponent size={48} style={{ filter: 'drop-shadow(0 0 10px #00a8ff)' }} />
                </div>
                <p className="text-xs text-center text-[#00a8ff] font-semibold break-all group-hover:text-white transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>
                  {name}
                </p>
                {copiedIcon === name && (
                  <div className="absolute inset-0 bg-[#00a8ff] bg-opacity-90 flex items-center justify-center rounded-xl">
                    <div className="text-[#0a2540] font-bold flex items-center gap-2">
                      <LucideIcons.CheckCircle size={24} />
                      <span>Copied!</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredIcons.length === 0 && (
          <div className="text-center py-20">
            <LucideIcons.Search className="text-[#00a8ff] mx-auto mb-4" size={64} style={{ filter: 'drop-shadow(0 0 20px #00a8ff)' }} />
            <p className="text-[#00a8ff] text-xl" style={{ textShadow: '0 0 8px #00a8ff' }}>
              No icons found matching "{searchTerm}"
            </p>
          </div>
        )}

        {/* Usage Instructions */}
        <Card className="mt-12 bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-white mb-4 led-text" style={{ textShadow: '0 0 10px #00a8ff' }}>
              How to Use Icons
            </h2>
            <div className="space-y-3 text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>
              <p className="flex items-start gap-3">
                <LucideIcons.CheckCircle className="flex-shrink-0 mt-1" size={20} />
                <span><strong>Click any icon</strong> to copy its import code and usage example</span>
              </p>
              <p className="flex items-start gap-3">
                <LucideIcons.CheckCircle className="flex-shrink-0 mt-1" size={20} />
                <span><strong>Search</strong> by icon name to quickly find what you need</span>
              </p>
              <p className="flex items-start gap-3">
                <LucideIcons.CheckCircle className="flex-shrink-0 mt-1" size={20} />
                <span><strong>Customize</strong> icon size using the size prop: <code className="bg-[rgba(0,168,255,0.2)] px-2 py-1 rounded">size={"{24}"}</code></span>
              </p>
              <p className="flex items-start gap-3">
                <LucideIcons.CheckCircle className="flex-shrink-0 mt-1" size={20} />
                <span><strong>Style</strong> icons with className or style props for colors and effects</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
